/**
 * PortfolioModulePage — /portfolio-kit/modules/:slug
 *
 * Renders one Portfolio Kit curriculum module. Metadata comes from
 * portfolioKitModules.ts; the markdown body is fetched from public/ and rendered
 * via <LessonMarkdown> (the same renderer as the Agentic Mastery lessons).
 */
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LessonMarkdown from "../components/LessonMarkdown";
import {
  PORTFOLIO_MODULES,
  fetchPortfolioModuleMarkdown,
  getPortfolioModuleBySlug,
} from "../data/portfolioKitModules";
import { L } from "../utils/localized";

export default function PortfolioModulePage() {
  const { t, i18n } = useTranslation("pages-products");
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const portfolioModules = L(PORTFOLIO_MODULES, i18n.language);
  const mod = getPortfolioModuleBySlug(slug, i18n.language);
  const index = portfolioModules.findIndex((m) => m.slug === slug);
  const prev = index > 0 ? portfolioModules[index - 1] : null;
  const next =
    index >= 0 && index < portfolioModules.length - 1
      ? portfolioModules[index + 1]
      : null;

  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mod) return;
    let cancelled = false;
    setMarkdown(null);
    setError(null);
    fetchPortfolioModuleMarkdown(mod.mdPath)
      .then((text) => !cancelled && setMarkdown(text))
      .catch(() => !cancelled && setError(t("PortfolioModulePage.loadErrorMessage")));
    return () => {
      cancelled = true;
    };
  }, [mod, t]);

  if (!mod) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t("PortfolioModulePage.moduleNotFoundMessage")}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/portfolio-kit")}>
          {t("PortfolioModulePage.backToPortfolioKitButton")}
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/portfolio-kit")}
          sx={{ mb: 3, textTransform: "none", color: "text.secondary" }}
        >
          {t("PortfolioModulePage.backToPortfolioKitButton")}
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Chip
            label={mod.number}
            size="small"
            sx={{
              fontWeight: 600,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.background.paper,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t("PortfolioModulePage.readMinutesLabel", { count: mod.readMinutes })}
          </Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>}

        {!markdown && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress />
          </Box>
        )}

        {markdown && <LessonMarkdown>{markdown}</LessonMarkdown>}

        {markdown && (
          <>
            <Divider sx={{ my: 5 }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              {prev ? (
                <Button
                  component={RouterLink}
                  to={`/portfolio-kit/modules/${prev.slug}`}
                  startIcon={<ArrowBackIcon />}
                  sx={{ textTransform: "none" }}
                >
                  {prev.number}: {prev.title}
                </Button>
              ) : (
                <span />
              )}
              {next && (
                <Button
                  component={RouterLink}
                  to={`/portfolio-kit/modules/${next.slug}`}
                  endIcon={<ArrowForwardIcon />}
                  variant="contained"
                  sx={{ textTransform: "none" }}
                >
                  {next.number}: {next.title}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Container>
    </Box>
  );
}
